import React, { useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = React.forwardRef((props, ref) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Mock submission
    setTimeout(() => {
      toast.success('Message sent! We\'ll be in touch within 24 hours.');
      setFormData({ name: '', email: '', projectType: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };
  
  return (
    <section ref={ref} className="py-20 px-4 bg-[#040c14] relative overflow-hidden">
      {/* Abstract background elements */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="abstract-blob animate-float" style={{ top: '10%', left: '5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(255, 101, 0, 0.2), transparent)' }}></div>
        <div className="abstract-blob animate-float-delayed" style={{ bottom: '10%', right: '10%', width: '350px', height: '350px', background: 'radial-gradient(circle, rgba(255, 140, 50, 0.15), transparent)' }}></div>
      </div>
      
      <div className="max-w-5xl mx-auto relative z-10">
        <h2 className="text-4xl md:text-6xl font-bold text-white mb-4 text-center">
          Let's Talk <span className="text-[#FF6500]">Strategy</span>
        </h2>
        <p className="text-xl text-[#aaa] text-center mb-16 max-w-3xl mx-auto">
          Ready to turn your story into screen-ready IP? Let's discuss your project.
        </p>
        
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">Get in Touch</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-[#FF6500] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium mb-1">Email</p>
                    <a 
                      href="mailto:harrymann.intl@gmail.com"
                      className="text-[#aaa] hover:text-[#FF6500] transition-colors duration-300"
                    >
                      harrymann.intl@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Or Book a Call</h3>
              <p className="text-[#aaa] mb-6 leading-relaxed">
                Schedule a 30-minute strategy session to discuss your book-to-film vision.
              </p>
              <a 
                href="#calendly"
                className="inline-block px-8 py-4 bg-transparent hover:bg-white/10 text-white border-2 border-white/20 hover:border-white/40 font-medium rounded-full transition-all duration-300 text-base uppercase tracking-wider"
              >
                View Calendar
              </a>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="bg-[#0a1525] p-8 rounded-lg border border-[#152840]">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white text-sm font-medium mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#040c14] border border-[#152840] rounded-lg text-white focus:outline-none focus:border-[#FF6500] transition-colors duration-300"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-white text-sm font-medium mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#040c14] border border-[#152840] rounded-lg text-white focus:outline-none focus:border-[#FF6500] transition-colors duration-300"
                  placeholder="your.email@example.com"
                />
              </div>
              
              <div>
                <label htmlFor="projectType" className="block text-white text-sm font-medium mb-2">
                  Project Type
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#040c14] border border-[#152840] rounded-lg text-white focus:outline-none focus:border-[#FF6500] transition-colors duration-300"
                >
                  <option value="">Select a project type</option>
                  <option value="proof-of-concept">Proof-of-Concept Trailer</option>
                  <option value="short-form">Short-Form Content</option>
                  <option value="design-bible">Visual Development Bible</option>
                  <option value="full-package">Full Package</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-white text-sm font-medium mb-2">
                  Tell us about your project *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-[#040c14] border border-[#152840] rounded-lg text-white focus:outline-none focus:border-[#FF6500] transition-colors duration-300 resize-none"
                  placeholder="Tell us about your book, vision, and goals..."
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#FF6500] hover:bg-[#E65A00] disabled:bg-[#FF6500]/50 text-black font-medium rounded-full transition-all duration-300 flex items-center justify-center gap-3 text-base uppercase tracking-wider"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
});

export default ContactSection;